/**
 * Hook for managing report submission state and logic
 *
 * Refactored to use extracted hooks:
 * - useReportForm: Form fields, image upload state, and validation
 * - useSubmissionBadges: Achievement badge detection around submission
 *
 * Payload construction lives in util/reports/buildReport.js.
 *
 * This hook now focuses on:
 * - Organization/routing state and QR sign-in
 * - Limited-account report counting
 * - The submission flow itself
 */
import { useState, useCallback } from 'react';

import Util from '../util/util';
import {
  getRouting as getStoredRouting,
  getOrganizationAccess,
} from '../util/storage';
import { useErrorState, showSuccess } from '../util/error';
import { trackEngagementAsync } from '../util/engagementTracker';
import { buildReportPayload } from '../util/reports/buildReport';

import useReportForm from './useReportForm';
import useSubmissionBadges from './useSubmissionBadges';
import { t } from '../i18n';

const LIMITED_REPORT_COUNT = 3;

export default function useReportSubmission(organization, noLogin) {
  // Error state
  const { error, hasError, handleError, clearError } = useErrorState({
    context: 'ReportSubmission',
    showToasts: true,
  });

  // Submission state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitEnabled, setSubmitEnabled] = useState(false);

  // Organization state
  const [routing, setRouting] = useState([]);
  const [validOrgs, setValidOrgs] = useState([]);
  const [OrgFilterOpen, setOrgFilterOpen] = useState(false);
  const [OrgFilterValue, setOrgFilterValue] = useState(organization);
  const [OrgFilter, setOrgFilter] = useState([]);

  // Limited account state
  const [reportCount, setReportCount] = useState(0);

  // Use extracted form hook (clears tag chips when the selected org changes)
  const form = useReportForm(OrgFilterValue);

  // Use extracted badge hook
  const badges = useSubmissionBadges();

  /**
   * Execute QR sign-in
   */
  const executeSignIn = useCallback(async () => {
    try {
      await Util.executeQRSignIn(organization, noLogin);
    } catch (err) {
      const message = err.toString();
      if (!message.includes('Pending sign-in')) {
        handleError(err, { userMessage: t('reports:ticket.signInFailed') });
      }
    }
  }, [organization, noLogin, handleError]);

  /**
   * Get routing configuration from storage
   */
  const getRouting = useCallback(async () => {
    const routingData = await getStoredRouting();
    setRouting(routingData);
  }, []);

  /**
   * Update valid organizations from storage
   */
  const updateValidOrgs = useCallback(async () => {
    let organization_access = await getOrganizationAccess();
    organization_access = organization_access.length ? organization_access : [organization];
    setValidOrgs(organization_access);
    setOrgFilter(organization_access.map((x) => ({ label: x, value: x })));
  }, [organization]);

  /**
   * Get user's report count for limited accounts
   */
  const getUserReportCount = useCallback(async () => {
    const requestData = {
      queryParams: {
        organization,
        organizations: organization,
      },
    };
    try {
      const data = await Util.get('Resty', '/items/listUserObjects/:user', requestData);
      const count = data.items.length;
      setReportCount(count);
      if (count < LIMITED_REPORT_COUNT) {
        setSubmitEnabled(true);
      }
    } catch (err) {
      handleError(err, { userMessage: t('reports:ticket.countFetchFailed'), silent: true });
    }
  }, [organization, handleError]);

  /**
   * Submit the report
   */
  const submitReport = useCallback(async (tryUploadImage = false, onSuccess) => {
    if (isSubmitting || !submitEnabled) return;

    form.setDisplayErrors(true);
    if (!form.isFormValid) return;

    setIsSubmitting(true);

    // Store badge count before submission for comparison
    await badges.storeBadgeCount();

    try {
      const [fullImageURLs, fulfilledImageURLs] = await Util.getUploadedImageURLs(
        tryUploadImage,
        form.uploadedImages,
        form.imagesToSend,
        form.setUploadedImages,
        form.setUploadStage,
        form.setUploadProgress,
      );

      const userEmail = await Util.getUserEmail();
      const { reportData, routedOrg } = buildReportPayload({
        description: form.description,
        email: form.email,
        anonymous: form.anonymous,
        isUrgent: form.isUrgent,
        isTest: form.isTest,
        KPIS: form.KPIS,
        Locations: form.Locations,
        routing,
        OrgFilterValue,
        organization,
        userEmail,
        fullImageURLs,
        fulfilledImageURLs,
      });

      await Util.post('Resty', '/items', { body: reportData });

      showSuccess(t('reports:ticket.sent'));

      // Track report submission for surprise badges (shows toast if badge earned)
      trackEngagementAsync('report_submitted');

      // Track image uploads for surprise badges (shows toast if badge earned)
      // Track each image separately
      if (fulfilledImageURLs.length > 0) {
        for (let i = 0; i < fulfilledImageURLs.length; i++) {
          trackEngagementAsync('image_upload');
        }
      }

      form.resetForm();
      setIsSubmitting(false);

      // Check for new badges after a delay (allows Lambda to process)
      badges.scheduleBadgeCheck();

      if (onSuccess) {
        onSuccess(reportData, routedOrg);
      }
    } catch (err) {
      handleError(err, { userMessage: t('reports:ticket.submitFailed') });
      setIsSubmitting(false);
    }
  }, [
    isSubmitting,
    submitEnabled,
    form,
    badges,
    routing,
    OrgFilterValue,
    organization,
    handleError,
  ]);

  return {
    // Error state
    error,
    hasError,
    clearError,

    // Form state (from form hook)
    description: form.description,
    setDescription: form.setDescription,
    email: form.email,
    setEmail: form.setEmail,
    anonymous: form.anonymous,
    setAnonymous: form.setAnonymous,
    isUrgent: form.isUrgent,
    setIsUrgent: form.setIsUrgent,
    isTest: form.isTest,
    setIsTest: form.setIsTest,
    KPIS: form.KPIS,
    setKPIS: form.setKPIS,
    Locations: form.Locations,
    setLocations: form.setLocations,

    // Image state (from form hook)
    imagesToSend: form.imagesToSend,
    setImagesToSend: form.setImagesToSend,
    uploadedImages: form.uploadedImages,
    uploadProgress: form.uploadProgress,
    uploadStage: form.uploadStage,
    setUploadStage: form.setUploadStage,
    imageLoading: form.imageLoading,
    setImageLoading: form.setImageLoading,
    removeImage: form.removeImage,

    // Validation state (from form hook)
    errors: form.errors,
    isFormValid: form.isFormValid,
    displayErrors: form.displayErrors,

    // Submission state
    isSubmitting,
    submitEnabled,
    setSubmitEnabled,

    // Organization state
    routing,
    validOrgs,
    OrgFilterOpen,
    setOrgFilterOpen,
    OrgFilterValue,
    setOrgFilterValue,
    OrgFilter,
    setOrgFilter,

    // Limited account
    reportCount,
    limitedReportCount: LIMITED_REPORT_COUNT,

    // Actions
    executeSignIn,
    getRouting,
    updateValidOrgs,
    getUserReportCount,
    submitReport,

    // Badge state (from badges hook)
    earnedBadge: badges.earnedBadge,
    showBadgeModal: badges.showBadgeModal,
    dismissBadgeModal: badges.dismissBadgeModal,
  };
}
